/**
 * @file Frontend utilities — a1FuelEmbodiedEnergy.ts.
 */

import { A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR } from "../constants/a1EmbodiedEnergyFactors";
import type { FormDataResponse } from "../interfaces/formInterfaces";

/** Type definition — A1Fuel Embodied Energy Category. */
export type A1FuelEmbodiedEnergyCategory =
  | "petrol_diesel"
  | "coal"
  | "direct_kwh"
  | "unknown";

/** Type definition — A1Fuel Quantity Unit Kind. */
export type A1FuelQuantityUnitKind =
  | "liter"
  | "kg"
  | "metric_ton"
  | "kwh"
  | "unknown";

/** Type definition — A1Fuel Density Unit Kind. */
export type A1FuelDensityUnitKind = "kg_per_l" | "kg_per_m3" | "unknown";

/** Type definition — A1Fuel Row Calc Inputs. */
export type A1FuelRowCalcInputs = {
  /** Dropdown value or unit token for fuel quantity */
  quantityUnitValue?: string;
  /** Resolved label/token (L, kg, MT, kWh, …) */
  quantityUnitLabel?: string;
  fuelDensity?: number;
  densityUnitLabel?: string;
};

const MJ_PER_KWH = 3.6;

function parseNum(raw: string | undefined): number {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

function normalizeUnitBlob(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/** Match fuel mix (dropdown value + label) to calculation path. */
export function classifyFuelMixForA1EmbodiedEnergy(
  mixValue: string,
  mixDropdownLabel: string
): A1FuelEmbodiedEnergyCategory {
  const blob = `${mixValue} ${mixDropdownLabel}`.toLowerCase();
  const compact = blob.replace(/\s+/g, "");

  if (
    /\b(solar|wind|hydro)\b/.test(blob) ||
    /\b(electric|electricity)\b/.test(blob) ||
    compact.includes("solar") ||
    compact.includes("wind") ||
    compact.includes("hydro") ||
    compact.includes("electric")
  ) {
    return "direct_kwh";
  }

  if (/\bcoal\b/.test(blob) || compact.includes("coal")) {
    return "coal";
  }

  if (
    /\b(petrol|gasoline|diesel)\b/.test(blob) ||
    compact.includes("petrol") ||
    compact.includes("gasoline") ||
    compact.includes("diesel")
  ) {
    return "petrol_diesel";
  }

  return "unknown";
}

/** Exported function — classify A1Fuel Quantity Unit. */
export function classifyA1FuelQuantityUnit(
  unitLabelOrToken: string
): A1FuelQuantityUnitKind {
  const u = normalizeUnitBlob(unitLabelOrToken);
  if (!u) return "unknown";

  if (u.includes("kwh") || u.includes("kilowatt")) return "kwh";
  if (
    u === "l" ||
    u.includes("liter") ||
    u.includes("litre") ||
    /\bl\b/.test(unitLabelOrToken.toLowerCase())
  ) {
    return "liter";
  }
  if (
    u.includes("mt") ||
    u.includes("metricton") ||
    u.includes("metrictonne") ||
    (u.includes("ton") && !u.includes("kwh") && !u.includes("kilowatt"))
  ) {
    return "metric_ton";
  }
  if (u.includes("kg") || u.includes("kilogram")) return "kg";

  return "unknown";
}

/** Exported function — classify A1Fuel Density Unit. */
export function classifyA1FuelDensityUnit(
  unitLabelOrToken: string
): A1FuelDensityUnitKind {
  const u = normalizeUnitBlob(unitLabelOrToken);
  if (!u) return "unknown";
  if (u.includes("m3") || u.includes("m³") || u.includes("cubic")) {
    return "kg_per_m3";
  }
  if (u.includes("kg") && (u.includes("l") || u.endsWith("/l"))) {
    return "kg_per_l";
  }
  if (u.includes("perl") || u === "kg/l") return "kg_per_l";
  return "unknown";
}

/** Resolve CMS unit dropdown value to a human-readable token/label. */
export function resolveA1UnitLabelFromValue(
  unitValue: string,
  dropdownOptions?: { value?: string | number; label?: string }[],
  unitTokenById?: Record<string, string>
): string {
  const raw = String(unitValue ?? "").trim();
  if (!raw) return "";

  if (/^[0-9]+$/.test(raw) && unitTokenById?.[raw]) {
    return unitTokenById[raw];
  }

  const opts = dropdownOptions ?? [];
  const hit = opts.find((o) => String(o.value ?? "") === raw);
  if (hit?.label) return String(hit.label);
  if (hit?.value != null && !/^[0-9]+$/.test(String(hit.value))) {
    return String(hit.value);
  }

  return raw;
}

function quantityToKg(
  qty: number,
  unitKind: A1FuelQuantityUnitKind
): number | null {
  if (!Number.isFinite(qty) || qty < 0) return null;
  switch (unitKind) {
    case "kg":
      return qty;
    case "metric_ton":
      // 1 metric ton = 1000 kg (flowchart: convert MT → kg)
      return qty * 1000;
    default:
      return null;
  }
}

function quantityLitersToKg(
  qtyLiters: number,
  density: number,
  densityUnit: A1FuelDensityUnitKind
): number | null {
  if (!Number.isFinite(qtyLiters) || qtyLiters < 0) return null;
  if (!Number.isFinite(density) || density <= 0) return null;

  if (densityUnit === "kg_per_m3") {
    return (qtyLiters / 1000) * density;
  }
  // Default: kg/L
  return qtyLiters * density;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Embodied energy in MJ per Form A1 flowcharts.
 */
export function computeA1EmbodiedEnergyMjFromFuelRow(
  category: A1FuelEmbodiedEnergyCategory,
  fuelQty: number,
  calorificValueMjPerKg: number,
  extras?: A1FuelRowCalcInputs
): number | null {
  if (category === "unknown") return null;
  if (!Number.isFinite(fuelQty) || fuelQty < 0) return null;

  const qtyUnitLabel =
    extras?.quantityUnitLabel ??
    extras?.quantityUnitValue ??
    (category === "direct_kwh" ? "kWh" : "");
  const qtyUnitKind = classifyA1FuelQuantityUnit(qtyUnitLabel);

  if (category === "direct_kwh") {
    const kwh =
      qtyUnitKind === "kwh" || qtyUnitKind === "unknown" ? fuelQty : fuelQty;
    return round3(kwh * MJ_PER_KWH);
  }

  if (category === "coal" || category === "petrol_diesel") {
    if (!Number.isFinite(calorificValueMjPerKg) || calorificValueMjPerKg <= 0) {
      return null;
    }

    if (category === "petrol_diesel" && qtyUnitKind === "liter") {
      const density = extras?.fuelDensity;
      if (density == null || !Number.isFinite(density) || density <= 0) {
        return null;
      }
      const densityUnit = classifyA1FuelDensityUnit(
        extras?.densityUnitLabel ?? ""
      );
      if (densityUnit === "kg_per_m3") {
        const qtyKg = quantityLitersToKg(fuelQty, density, densityUnit);
        if (qtyKg == null || !Number.isFinite(qtyKg)) return null;
        return round3(qtyKg * calorificValueMjPerKg);
      }
      // L × density [kg/L] × calorific [MJ/kg] → MJ
      return round3(fuelQty * density * calorificValueMjPerKg);
    }

    const qtyKg = quantityToKg(fuelQty, qtyUnitKind);
    if (qtyKg == null || !Number.isFinite(qtyKg)) return null;
    // kg (or MT→kg) × calorific [MJ/kg] → MJ
    return round3(qtyKg * calorificValueMjPerKg);
  }

  return null;
}

/**
 * Embodied energy (kWh) for one A1 fuel row.
 *
 * Petrol/diesel — qty [L]: × density [kg/L] × calorific [MJ/kg]; qty [kg]: × calorific [MJ/kg]; → MJ, then ÷ 3.6.
 * Coal — qty [MT]→kg (×1000) or [kg]; × calorific [MJ/kg] → MJ, then ÷ 3.6.
 * Electricity / solar / hydro — qty [kWh] × 3.6 → MJ, stored as kWh (= qty).
 */
export function computeA1EmbodiedEnergyKwhFromFuelRow(
  category: A1FuelEmbodiedEnergyCategory,
  fuelQty: number,
  calorificValue: number,
  /** @deprecated Legacy MJ→kWh shortcut; ignored when extras provide unit/density context. */
  petrolDieselConversionFactor: number = A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR,
  extras?: A1FuelRowCalcInputs
): number | null {
  const mj = computeA1EmbodiedEnergyMjFromFuelRow(
    category,
    fuelQty,
    calorificValue,
    extras
  );
  if (mj != null) {
    return round3(mj / MJ_PER_KWH);
  }

  // Legacy fallback (no unit/density): petrol/diesel qty assumed kg-equivalent
  if (category === "petrol_diesel") {
    if (!Number.isFinite(calorificValue) || calorificValue <= 0) return null;
    const factor =
      Number.isFinite(petrolDieselConversionFactor) &&
      petrolDieselConversionFactor > 0
        ? petrolDieselConversionFactor
        : A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR;
    return round3(fuelQty * calorificValue * factor);
  }

  if (category === "direct_kwh") {
    return round3(fuelQty);
  }

  return null;
}

/**
 * Reference embodied carbon [kg CO2eq] per Form A1.
 *
 * Petrol/diesel: embodied energy [MJ] × characterization factor
 * Coal (kg): fuel qty [kg] × characterization factor
 * Coal (MT): fuel qty × 1000 × characterization factor
 * Electricity / solar / wind: fuel qty [kWh] × characterization factor
 */
export function computeA1EmbodiedCarbonReferenceKgFromFuelRow(params: {
  category: A1FuelEmbodiedEnergyCategory;
  fuelQty: number;
  calorificValueMjPerKg: number;
  cfValue: number;
  /** @deprecated Unit-aware conversion removed; direct multiply per A1 formulae. */
  cfUnitLabel?: string;
  energyMj?: number;
  extras?: A1FuelRowCalcInputs;
}): number | null {
  const {
    category,
    fuelQty,
    calorificValueMjPerKg,
    cfValue,
    energyMj,
    extras,
  } = params;

  if (category === "unknown") return null;
  if (!Number.isFinite(cfValue) || cfValue <= 0) return null;

  if (category === "petrol_diesel") {
    const eeMj =
      Number.isFinite(energyMj) && energyMj != null && energyMj >= 0
        ? energyMj
        : computeA1EmbodiedEnergyMjFromFuelRow(
            category,
            fuelQty,
            calorificValueMjPerKg,
            extras
          );
    if (eeMj == null || !Number.isFinite(eeMj)) return null;
    return round3(eeMj * cfValue);
  }

  if (category === "coal") {
    if (!Number.isFinite(fuelQty) || fuelQty < 0) return null;
    const qtyUnitLabel =
      extras?.quantityUnitLabel ?? extras?.quantityUnitValue ?? "";
    const qtyUnitKind = classifyA1FuelQuantityUnit(qtyUnitLabel);
    const qtyKg = quantityToKg(fuelQty, qtyUnitKind);
    if (qtyKg == null || !Number.isFinite(qtyKg)) return null;
    return round3(qtyKg * cfValue);
  }

  if (category === "direct_kwh") {
    if (!Number.isFinite(fuelQty) || fuelQty < 0) return null;
    return round3(fuelQty * cfValue);
  }

  return null;
}

function getFuelMixDropdownLabelFromForm(
  formData: FormDataResponse,
  mixValue: string
): string {
  const fuelSec = formData.formSubSections?.find(
    (s) => s.code === "A1_FUEL_EXTRACTION"
  );
  const field = fuelSec?.fields?.find((f) => f.name === "fuelMix_extraction");
  const opts = field?.dropdown?.options || field?.dropdownValues || [];
  const hit = opts.find(
    (o: { value?: string | number; label?: string }) =>
      String(o.value) === String(mixValue)
  );
  return String(hit?.label ?? hit?.value ?? "");
}

function getFieldDropdownOptions(
  formData: FormDataResponse,
  sectionCode: string,
  fieldName: string
): { value?: string | number; label?: string }[] {
  const sec = formData.formSubSections?.find((s) => s.code === sectionCode);
  const field = sec?.fields?.find((f) => f.name === fieldName);
  return field?.dropdown?.options || field?.dropdownValues || [];
}

/** Type definition — A1Embodied Energy Build Options. */
export type A1EmbodiedEnergyBuildOptions = {
  /** Default embodied-energy unit when auto-calculating (numeric unit id preferred). */
  mjUnitId?: string | null;
  /** @deprecated A1 auto-calc uses MJ; kept for callers that still pass kWh id. */
  kWhUnitId?: string | null;
  /** Numeric unit id → token (kWh, L, kg, …) */
  unitTokenById?: Record<string, string>;
};

/** Resolve MJ unit id for embodied-energy `<select>` (numeric id when units index is loaded). */
export function resolveA1EmbodiedEnergyMjUnitSelectValue(
  unitIdByToken: Record<string, number>
): string {
  const tryToken = (t: string) => {
    const id = unitIdByToken[t];
    return id != null && Number.isFinite(Number(id)) ? String(id) : null;
  };
  return (
    tryToken("mj") ??
    tryToken("megajoule") ??
    tryToken("megajoules") ??
    "MJ"
  );
}

/**
 * Updates for `embodiedEnergy_extraction_*` and `embodiedEnergy_unit_extraction_*`
 * from A1 fuel rows (same row index as raw material).
 */
export function buildA1EmbodiedEnergyValueUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  sectionRows: Record<string, number>,
  options?: A1EmbodiedEnergyBuildOptions
): Record<string, string> {
  if (!formData) return {};

  const fuelSec = formData.formSubSections?.find(
    (s) => s.code === "A1_FUEL_EXTRACTION"
  );
  const energySec = formData.formSubSections?.find(
    (s) => s.code === "A1_EMBODIED_ENERGY"
  );
  if (!fuelSec?.code || !energySec?.code) return {};

  const fuelCode = fuelSec.code;
  const energyCode = energySec.code;
  const rows = sectionRows[fuelCode] ?? sectionRows[energyCode] ?? 1;

  const updates: Record<string, string> = {};
  const unitTokenById = options?.unitTokenById ?? {};
  const mjUnit = options?.mjUnitId?.trim() || "MJ";

  const qtyUnitOpts = getFieldDropdownOptions(
    formData,
    fuelCode,
    "fuelQuantity_unit_extraction"
  );
  const densityUnitOpts = getFieldDropdownOptions(
    formData,
    fuelCode,
    "fuelDensity_unit_extraction"
  );

  for (let i = 0; i < rows; i++) {
    const mix = String(
      values[`fuelMix_extraction_${fuelCode}_row${i}`] ?? ""
    ).trim();
    const qty = parseNum(
      values[`fuelQuantity_extraction_${fuelCode}_row${i}`]
    );
    const cal = parseNum(
      values[`calorificValue_extraction_${fuelCode}_row${i}`]
    );
    const qtyUnitRaw =
      values[`fuelQuantity_unit_extraction_${fuelCode}_row${i}`] ?? "";
    const density = parseNum(
      values[`fuelDensity_extraction_${fuelCode}_row${i}`]
    );
    const densityUnitRaw =
      values[`fuelDensity_unit_extraction_${fuelCode}_row${i}`] ?? "";

    const eKey = `embodiedEnergy_extraction_${energyCode}_row${i}`;
    const uKey = `embodiedEnergy_unit_extraction_${energyCode}_row${i}`;

    if (!mix) {
      updates[eKey] = "";
      updates[uKey] = mjUnit;
      continue;
    }

    const label = getFuelMixDropdownLabelFromForm(formData, mix);
    const cat = classifyFuelMixForA1EmbodiedEnergy(mix, label);
    const extras: A1FuelRowCalcInputs = {
      quantityUnitValue: qtyUnitRaw,
      quantityUnitLabel: resolveA1UnitLabelFromValue(
        qtyUnitRaw,
        qtyUnitOpts,
        unitTokenById
      ),
      fuelDensity: Number.isFinite(density) ? density : undefined,
      densityUnitLabel: resolveA1UnitLabelFromValue(
        densityUnitRaw,
        densityUnitOpts,
        unitTokenById
      ),
    };
    const mj = computeA1EmbodiedEnergyMjFromFuelRow(cat, qty, cal, extras);

    if (mj == null) {
      updates[eKey] = "";
      updates[uKey] = mjUnit;
    } else {
      updates[eKey] = String(mj);
      updates[uKey] = mjUnit;
    }
  }

  return updates;
}

/**
 * Default empty embodied-energy unit to MJ (A1 Embodied Energy Details).
 */
export function buildDefaultA1EmbodiedEnergyUnitUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  sectionRows: Record<string, number>,
  options?: A1EmbodiedEnergyBuildOptions
): Record<string, string> {
  if (!formData) return {};

  const energySec = formData.formSubSections?.find(
    (s) => s.code === "A1_EMBODIED_ENERGY"
  );
  if (!energySec?.code) return {};

  const code = energySec.code;
  const rows = sectionRows[code] ?? 1;
  const updates: Record<string, string> = {};
  const mjUnit = options?.mjUnitId?.trim() || "MJ";

  for (let i = 0; i < rows; i++) {
    const uKey = `embodiedEnergy_unit_extraction_${code}_row${i}`;
    const v = String(values[uKey] ?? "").trim();
    if (/^[0-9]+$/.test(v)) continue;
    if (v === "kWh") continue;
    if (v === "" || v === "MJ") {
      if (v !== mjUnit) updates[uKey] = mjUnit;
    }
  }

  return updates;
}
